import { useState, useEffect, useRef, useCallback } from 'react';
import { Gift, Plus, Edit2, Trash2, Eye, CheckCircle, XCircle, Power, PowerOff, Calendar, Bold, Italic } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { formatDistanceToNow } from '../utils/date-utils';
import { WelcomeModal as WelcomeModalComponent } from './WelcomeModal';
import { devError } from '../utils/logger';


interface AdminWelcomeModalsProps {
  accessToken: string;
}

type FontSizeKey = 'sm' | 'md' | 'lg' | 'xl';
type FontStyleKey = 'regular' | 'bold' | 'italic';

interface TextFieldStyle {
  size?: FontSizeKey;
  style?: FontStyleKey;
}

interface WelcomeModalTextStyles {
  title?: TextFieldStyle;
  subtitle?: TextFieldStyle;
  message?: TextFieldStyle;
  cta?: TextFieldStyle;
}

interface WelcomeModalData {
  id: string;
  title: string;
  subtitle?: string;
  message: string;
  icon: string;
  ctaText: string;
  triggerType: 'first_visit' | 'every_visit' | 'first_visit_timed';
  showWhen?: 'on_landing' | 'after_login';
  showFrom?: string;
  showUntil?: string;
  isActive: boolean;
  allowClose?: boolean;
  showButton?: boolean;
  textStyles?: WelcomeModalTextStyles | null;
  createdAt: string;
  updatedAt: string;
}

export function AdminWelcomeModals({ accessToken }: AdminWelcomeModalsProps) {
  const adminModalZ = useAdminModalZIndex();
  const [modals, setModals] = useState<WelcomeModalData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingModal, setEditingModal] = useState<WelcomeModalData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deletingModalId, setDeletingModalId] = useState<string | null>(null);
  const [previewModal, setPreviewModal] = useState<WelcomeModalData | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [message, setMessage] = useState('');
  const [icon, setIcon] = useState('Sparkles');
  const [ctaText, setCtaText] = useState('Kom i gang');
  const [triggerType, setTriggerType] = useState<'first_visit' | 'every_visit' | 'first_visit_timed'>('first_visit');
  const [showWhen, setShowWhen] = useState<'on_landing' | 'after_login'>('on_landing');
  const [showFrom, setShowFrom] = useState('');
  const [showUntil, setShowUntil] = useState('');
  const [allowClose, setAllowClose] = useState(true);
  const [showButton, setShowButton] = useState(true);
  const [textStyles, setTextStyles] = useState<WelcomeModalTextStyles>({});

  const titleInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const ctaInputRef = useRef<HTMLInputElement>(null);

  const wrapSelectionInMarkdown = useCallback(
    (
      ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
      before: string,
      after: string,
      value: string,
      setValue: (v: string) => void
    ) => {
      const el = ref.current;
      if (!el) return;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? start;
      const newValue = value.slice(0, start) + before + value.slice(start, end) + after + value.slice(end);
      setValue(newValue);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + before.length, end + before.length);
      });
    },
    []
  );

  useEffect(() => {
    fetchModals();
  }, []);

  const fetchModals = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/welcome-modals`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setModals(data.modals || []);
      } else {
        toast.error('Kunne ikke hente velkomstmodaler');
      }
    } catch (error) {
      devError('Error fetching welcome modals:', error);
      toast.error('Feil ved henting av velkomstmodaler');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!title.trim() || !message.trim() || !ctaText.trim()) {
      toast.error('Tittel, melding og knapp-tekst er påkrevd');
      return;
    }

    // Validate timed triggers have at least one date
    if ((triggerType === 'first_visit_timed' || triggerType === 'every_visit') && !showFrom && !showUntil) {
      toast.error('Tidsbaserte modaler må ha minst én dato satt');
      return;
    }

    try {
      const url = editingModal
        ? `${API_BASE}/admin/welcome-modals/${editingModal.id}`
        : `${API_BASE}/admin/welcome-modals`;

      const response = await fetch(url, {
        method: editingModal ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          title,
          subtitle: subtitle || null,
          message,
          icon,
          cta_text: ctaText,
          trigger_type: triggerType,
          show_when: showWhen,
          show_from: showFrom || null,
          show_until: showUntil || null,
          allow_close: allowClose,
          show_button: showButton,
          text_styles: Object.keys(textStyles).length ? textStyles : null,
        }),
      });

      if (response.ok) {
        toast.success(editingModal ? 'Modal oppdatert!' : 'Modal opprettet!');
        resetForm();
        fetchModals();
      } else {
        toast.error('Kunne ikke lagre modal');
      }
    } catch (error) {
      devError('Error saving modal:', error);
      toast.error('Feil ved lagring');
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingModalId(id);
    try {
      const response = await fetch(
        `${API_BASE}/admin/welcome-modals/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Modal slettet!');
        fetchModals();
      } else {
        toast.error('Kunne ikke slette modal');
      }
    } catch (error) {
      devError('Error deleting modal:', error);
      toast.error('Feil ved sletting');
    } finally {
      setDeletingModalId(null);
      setShowDeleteConfirm(null);
    }
  };

  const handleToggleActive = async (id: string, currentlyActive: boolean) => {
    try {
      const endpoint = currentlyActive ? 'deactivate' : 'activate';
      const response = await fetch(
        `${API_BASE}/admin/welcome-modals/${id}/${endpoint}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success(currentlyActive ? 'Modal deaktivert!' : 'Modal aktivert!');
        fetchModals();
      } else {
        toast.error('Kunne ikke endre status');
      }
    } catch (error) {
      devError('Error toggling active:', error);
      toast.error('Feil ved endring av status');
    }
  };

  const resetForm = () => {
    setTitle('');
    setSubtitle('');
    setMessage('');
    setIcon('Sparkles');
    setCtaText('Kom i gang');
    setTriggerType('first_visit');
    setShowWhen('on_landing');
    setShowFrom('');
    setShowUntil('');
    setAllowClose(true);
    setShowButton(true);
    setTextStyles({});
    setEditingModal(null);
    setShowCreateModal(false);
  };

  const openEditModal = (modal: WelcomeModalData) => {
    setTitle(modal.title);
    setSubtitle(modal.subtitle || '');
    setMessage(modal.message);
    setIcon(modal.icon);
    setCtaText(modal.ctaText);
    setTriggerType(modal.triggerType);
    setShowWhen(modal.showWhen === 'after_login' ? 'after_login' : 'on_landing');
    setShowFrom(modal.showFrom ? modal.showFrom.split('T')[0] : '');
    setShowUntil(modal.showUntil ? modal.showUntil.split('T')[0] : '');
    setAllowClose(modal.allowClose !== undefined ? modal.allowClose : true);
    setShowButton(modal.showButton !== undefined ? modal.showButton : true);
    setTextStyles(modal.textStyles ?? {});
    setEditingModal(modal);
    setShowCreateModal(true);
  };

  const getTriggerTypeLabel = (modal: WelcomeModalData) => {
    if (modal.triggerType === 'first_visit') {
      return 'Førstegangsbesøk';
    }
    if (modal.triggerType === 'every_visit') {
      return 'Hvert besøk';
    }
    if (modal.triggerType === 'first_visit_timed') {
      const from = modal.showFrom ? new Date(modal.showFrom).toLocaleDateString('no-NO') : null;
      const until = modal.showUntil ? new Date(modal.showUntil).toLocaleDateString('no-NO') : null;
      
      if (from && until) return `${from} til ${until}`;
      if (from) return `Fra ${from}`;
      if (until) return `Til ${until}`;
      return 'Tidsbasert';
    }
    return modal.triggerType;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl text-gray-800">Welcome Modals</h2>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Ny Welcome Modal
          </button>
        </div>
        <p className="text-gray-600 text-sm">
          Lag velkomstmodaler som vises til førstegangsbesøkende eller i spesifikke tidsperioder.
        </p>
      </div>

      {/* Modals List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Loader size="lg" className="text-purple-600" />
            <p className="text-gray-600 mt-4">Laster velkomstmodaler...</p>
          </div>
        ) : modals.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Ingen velkomstmodaler ennå</p>
            <p className="text-gray-500 text-sm mt-2">Opprett din første welcome modal for å komme i gang</p>
          </div>
        ) : (
          modals.map((modal) => (
            <div
              key={modal.id}
              className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                modal.isActive
                  ? 'border-green-200 bg-green-50/50'
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">{modal.title}</h3>
                    {modal.isActive && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Aktiv
                      </span>
                    )}
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {getTriggerTypeLabel(modal)}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                      {(modal.showWhen ?? 'on_landing') === 'after_login' ? 'Etter innlogging' : 'Ved innkomst'}
                    </span>
                  </div>
                  {modal.subtitle && (
                    <p className="text-gray-500 text-sm mb-2">{modal.subtitle}</p>
                  )}
                  <p className="text-gray-600 mb-3 whitespace-pre-line">{modal.message}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Ikon: {modal.icon}</span>
                    <span>•</span>
                    <span>Knapp: "{modal.ctaText}"</span>
                    <span>•</span>
                    <span>{formatDistanceToNow(modal.createdAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleActive(modal.id, modal.isActive)}
                    className={`p-2 rounded-lg transition-colors ${
                      modal.isActive
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={modal.isActive ? 'Deaktiver' : 'Aktiver'}
                  >
                    {modal.isActive ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => openEditModal(modal)}
                    className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                    title="Rediger"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(modal.id)}
                    className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                    title="Slett"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview action */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => setPreviewModal(modal)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 rounded-xl hover:from-blue-200 hover:to-cyan-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Forhåndsvis Modal
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 'var(--z-important)' }}
            onClick={resetForm}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-important)' }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editingModal ? 'Rediger Welcome Modal' : 'Ny Welcome Modal'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Tittel *</label>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500">Merke tekst og bruk:</span>
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(titleInputRef, '**', '**', title, setTitle)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Fet (**tekst**)"
                    >
                      <Bold className="w-3.5 h-3.5" />
                      Fet
                    </button>
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(titleInputRef, '*', '*', title, setTitle)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Kursiv (*tekst*)"
                    >
                      <Italic className="w-3.5 h-3.5" />
                      Kursiv
                    </button>
                  </div>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="F.eks. Velkommen til Talentfunn!"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={100}
                  />
                  <div className="flex gap-3 mt-2">
                    <select
                      value={textStyles.title?.size ?? 'lg'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, title: { ...s.title, size: e.target.value as FontSizeKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="sm">Små</option>
                      <option value="md">Normal</option>
                      <option value="lg">Stor</option>
                      <option value="xl">Ekstra stor</option>
                    </select>
                    <select
                      value={textStyles.title?.style ?? 'bold'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, title: { ...s.title, style: e.target.value as FontStyleKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="regular">Normal</option>
                      <option value="bold">Fet</option>
                      <option value="italic">Kursiv</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Undertittel</label>
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(subtitleInputRef, '**', '**', subtitle, setSubtitle)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Fet"
                    >
                      <Bold className="w-3.5 h-3.5" />
                      Fet
                    </button>
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(subtitleInputRef, '*', '*', subtitle, setSubtitle)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Kursiv"
                    >
                      <Italic className="w-3.5 h-3.5" />
                      Kursiv
                    </button>
                  </div>
                  <input
                    ref={subtitleInputRef}
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="F.eks. Din nye jobbplattform"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={500}
                  />
                  <div className="flex gap-3 mt-2">
                    <select
                      value={textStyles.subtitle?.size ?? 'md'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, subtitle: { ...s.subtitle, size: e.target.value as FontSizeKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="sm">Små</option>
                      <option value="md">Normal</option>
                      <option value="lg">Stor</option>
                      <option value="xl">Ekstra stor</option>
                    </select>
                    <select
                      value={textStyles.subtitle?.style ?? 'regular'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, subtitle: { ...s.subtitle, style: e.target.value as FontStyleKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="regular">Normal</option>
                      <option value="bold">Fet</option>
                      <option value="italic">Kursiv</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Melding *</label>
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(messageInputRef, '**', '**', message, setMessage)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Fet"
                    >
                      <Bold className="w-3.5 h-3.5" />
                      Fet
                    </button>
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(messageInputRef, '*', '*', message, setMessage)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Kursiv"
                    >
                      <Italic className="w-3.5 h-3.5" />
                      Kursiv
                    </button>
                  </div>
                  <textarea
                    ref={messageInputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Skriv meldingen som brukerne vil se..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
                    maxLength={500}
                  />
                  <div className="flex gap-3 mt-2">
                    <select
                      value={textStyles.message?.size ?? 'md'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, message: { ...s.message, size: e.target.value as FontSizeKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="sm">Små</option>
                      <option value="md">Normal</option>
                      <option value="lg">Stor</option>
                      <option value="xl">Ekstra stor</option>
                    </select>
                    <select
                      value={textStyles.message?.style ?? 'regular'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, message: { ...s.message, style: e.target.value as FontStyleKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="regular">Normal</option>
                      <option value="bold">Fet</option>
                      <option value="italic">Kursiv</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Ikon *</label>
                  <select
                    value={icon}
                    onChange={(e) => setIcon(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Sparkles">Sparkles (Standard)</option>
                    <option value="Rocket">Rocket</option>
                    <option value="PartyPopper">Party Popper</option>
                    <option value="Gift">Gift</option>
                    <option value="Zap">Zap</option>
                    <option value="Star">Star</option>
                    <option value="Heart">Heart</option>
                    <option value="Bell">Bell</option>
                    <option value="Megaphone">Megaphone</option>
                    <option value="TrendingUp">Trending Up</option>
                    <option value="Award">Award</option>
                    <option value="Target">Target</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Knapp-tekst *</label>
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(ctaInputRef, '**', '**', ctaText, setCtaText)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Fet"
                    >
                      <Bold className="w-3.5 h-3.5" />
                      Fet
                    </button>
                    <button
                      type="button"
                      onClick={() => wrapSelectionInMarkdown(ctaInputRef, '*', '*', ctaText, setCtaText)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
                      title="Kursiv"
                    >
                      <Italic className="w-3.5 h-3.5" />
                      Kursiv
                    </button>
                  </div>
                  <input
                    ref={ctaInputRef}
                    type="text"
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="F.eks. Kom i gang"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={50}
                  />
                  <div className="flex gap-3 mt-2">
                    <select
                      value={textStyles.cta?.size ?? 'md'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, cta: { ...s.cta, size: e.target.value as FontSizeKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="sm">Små</option>
                      <option value="md">Normal</option>
                      <option value="lg">Stor</option>
                      <option value="xl">Ekstra stor</option>
                    </select>
                    <select
                      value={textStyles.cta?.style ?? 'regular'}
                      onChange={(e) => setTextStyles((s) => ({ ...s, cta: { ...s.cta, style: e.target.value as FontStyleKey } }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="regular">Normal</option>
                      <option value="bold">Fet</option>
                      <option value="italic">Kursiv</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Trigger *</label>
                  <select
                    value={triggerType}
                    onChange={(e) => setTriggerType(e.target.value as 'first_visit' | 'every_visit' | 'first_visit_timed')}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="first_visit">Førstegangsbesøk (globalt)</option>
                    <option value="every_visit">Hvert besøk (i tidsrom)</option>
                    <option value="first_visit_timed">Første besøk (i tidsrom)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Når skal modalen vises?</label>
                  <select
                    value={showWhen}
                    onChange={(e) => setShowWhen(e.target.value as 'on_landing' | 'after_login')}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="on_landing">Ved innkomst på siden (med en gang)</option>
                    <option value="after_login">Etter innlogging</option>
                  </select>
                </div>

                {(triggerType === 'first_visit_timed' || triggerType === 'every_visit') && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Vis fra</label>
                      <input
                        type="date"
                        value={showFrom}
                        onChange={(e) => setShowFrom(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Vis til</label>
                      <input
                        type="date"
                        value={showUntil}
                        onChange={(e) => setShowUntil(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <p className="col-span-2 text-gray-600 text-xs">
                      {triggerType === 'every_visit'
                        ? 'Minst én dato må være satt. Modalen vises ved hvert besøk innenfor tidsrommet.'
                        : 'Minst én dato må være satt. Modalen vises én gang ved brukerens første besøk innenfor tidsrommet.'}
                    </p>
                  </div>
                )}

                {/* ✅ NEW: Allow Close & Show Button toggles */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Tillat lukking</label>
                      <p className="text-gray-500 text-xs">Vis lukke-kryss (X)</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAllowClose(!allowClose)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        allowClose ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          allowClose ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Vis knapp</label>
                      <p className="text-gray-500 text-xs">Vis CTA-knappen</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowButton(!showButton)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        showButton ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showButton ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleCreateOrUpdate}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                  >
                    {editingModal ? 'Oppdater' : 'Opprett'}
                  </button>
                  <button
                    onClick={resetForm}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => showDeleteConfirm && handleDelete(showDeleteConfirm)}
        title="Slett welcome modal?"
        message="Er du sikker på at du vil slette denne welcome modal? Denne handlingen kan ikke angres."
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={!!deletingModalId}
        loadingText="Sletter..."
        portalZIndex={adminModalZ}
      />

      {/* Preview Modal */}
      {previewModal && (
        <WelcomeModalComponent
          isOpen={true}
          modal={previewModal}
          onClose={() => setPreviewModal(null)}
          portalZIndex={adminModalZ}
        />
      )}
    </div>
  );
}
